const RETURN_KEY = 'siesta-outlook-oauth-return';

// The consent round trip takes at most a few minutes; anything older is an abandoned flow
// whose key must not fire a surprise conversation reopen later in the tab's life.
const RETURN_TTL_MS = 15 * 60 * 1000;

type OutlookOAuthReturn = {
  path: string;
  conversationId: string;
  // Whether the page the user connected from publishes contentMeta once mounted. The reopen
  // after the redirect must wait for it, or the chat scope settles underneath the reloaded
  // conversation and displaces it.
  pageSetsContentMeta: boolean;
  // Set by the Connect Outlook card: after the redirect back, the chat auto-sends a
  // continuation message so the paused email-reading request carries on.
  autoResume?: boolean;
  // Set by the OAuth callback page once the authorisation code has been exchanged. Only the
  // callback page knows consent actually completed — a connection-status check cannot tell a
  // fresh consent from a stored token that Graph has since started rejecting.
  consentCompleted?: boolean;
  storedAt: number;
};

export function storeOutlookOAuthReturn(value: Omit<OutlookOAuthReturn, 'storedAt'>) {
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({ ...value, storedAt: Date.now() }));
}

export function peekOutlookOAuthReturn(): OutlookOAuthReturn | null {
  const raw = sessionStorage.getItem(RETURN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OutlookOAuthReturn;
    const valid =
      typeof parsed.path === 'string' &&
      typeof parsed.conversationId === 'string' &&
      typeof parsed.storedAt === 'number' &&
      Date.now() - parsed.storedAt <= RETURN_TTL_MS;
    if (!valid) {
      sessionStorage.removeItem(RETURN_KEY);
      return null;
    }
    return {
      ...parsed,
      pageSetsContentMeta: parsed.pageSetsContentMeta === true,
      autoResume: parsed.autoResume === true,
      consentCompleted: parsed.consentCompleted === true,
    };
  } catch {
    sessionStorage.removeItem(RETURN_KEY);
    return null;
  }
}

export function markOutlookOAuthConsentCompleted() {
  const pending = peekOutlookOAuthReturn();
  if (!pending) return;
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({ ...pending, consentCompleted: true }));
}

export function clearOutlookOAuthReturn() {
  sessionStorage.removeItem(RETURN_KEY);
}
